import { inject, nextTick, onMounted, ref, watch, type Ref } from "vue"
import { storeToRefs } from "pinia"
import { onClickOutside } from "@vueuse/core"
import ModelManager from "@/services/model-manager"
import { Location } from "@/models/location.model"
import { FileRelation } from "@/models/file/file-relation.model"
import { FileViewerController } from "@/controllers/file-viewer-controller"
import type { LocationController } from "@/controllers/model-controllers/location-controller"
import { useLocationStore } from "@/store/locations.store"
import { useKnownBusinessEntitiesContactsStore } from "@/store/known-business-entities-contacts.store"
import { useProjectStore } from "@/store/projects.store"
import { useKnownBusinessEntityStore } from "@/store/known-business-entities.store"
import { useModelFormStore } from "@/store/model-forms.store"
import { KnownBusinessEntityContact } from "@/models/known-business-entity-contact.model"
import { Project } from "@/models/project.model"
import { KnownBusinessEntity } from "@/models/known-business-entity.model"
import { Person } from "@/models/person.model"
import { ModelFactory } from "@/factories/model-factory"
import { BaseModel } from "@/models/base.model"
import { useAuthStore } from "@/store/auth.store"
import {
  closeMentionDetail,
  mentionDetail,
  openMentionDetail,
  shouldUseReadOnlyMentionDetailModal
} from "@/composables/mention/use-mention-detail"
import { Bus } from "@/services/bus"
import { v4 as uuidv4 } from "uuid"
import useDelayedFunction from "@/composables/input/delayed-function.ts"
import { ODataQueryBuilder } from "@/builder/odata-query-builder"
import {
  searchLocationSuggestions,
  mergeFirstSuggestionStreetWithHouseNumber,
  shouldApplyFirstSuggestionStreet,
  useSuggestionList,
  type LocationSuggestion
} from "@/composables/location/use-location-autocomplete"
import { fuzzySearchCollection } from "@/helper/collection-helper"
import {
  stripCountryFromLocationText,
  LOCATION_POINTER_HOVER_TITLE
} from "@/helper/location/location-helpers"
import "./pending-mention.css"

// --- Keypress triggers (location open/close) ---

const RECENT_KEY_LIMIT = 3
const LOCATION_MENTION_CLICK_DELAY_MS = 250

export function pushRecentKey(keys: string[], key: string): string[] {
  return [...keys, key].slice(-RECENT_KEY_LIMIT)
}

export function shouldClearRecentKeys(key: string): boolean {
  return key === "Backspace" || key === "Delete"
}

/** The last two keypresses are space; a third trailing space does not count. */
export function isDoubleSpaceGesture(keys: string[]): boolean {
  if (keys.length < 2) return false
  if (keys[keys.length - 1] !== " " || keys[keys.length - 2] !== " ") return false
  return keys.length < 3 || keys[keys.length - 3] !== " "
}

const LOCATION_TRIGGER = "/"
const queuedLocationMentionLoads = new Set<string>()
const locationMentionLoadPromises = new Map<string, Promise<void>>()
const locationMentionLoadResolvers = new Map<string, () => void>()
const loadedLocationMentionIds = new Set<string>()
let locationMentionLoadController: LocationController | undefined
let locationMentionLoadScheduled = false

function scheduleLocationMentionLoads() {
  if (locationMentionLoadScheduled) return
  locationMentionLoadScheduled = true

  queueMicrotask(async () => {
    locationMentionLoadScheduled = false
    const ids = [...queuedLocationMentionLoads]
    queuedLocationMentionLoads.clear()
    const controller = locationMentionLoadController
    locationMentionLoadController = undefined

    if (!controller || ids.length === 0) return

    try {
      const locations = await controller.getList({
        query: new ODataQueryBuilder().in("id", ids)
      })
      const foundIds = new Set(locations.map((location) => location.id).filter(Boolean))

      // A deleted or otherwise unavailable location can be absent from the list response.
      // Retain the existing detail-request behavior only for those exceptional IDs.
      await Promise.all(
        ids
          .filter((id) => !foundIds.has(id))
          .map(async (id) => {
            try {
              await controller.detail(id)
            } catch {
              /* unresolved labels fall back to a placeholder */
            }
          })
      )
      ids.forEach((id) => loadedLocationMentionIds.add(id))
    } catch {
      // Preserve the original behavior if batching itself is unavailable.
      await Promise.all(
        ids.map(async (id) => {
          try {
            await controller.detail(id)
            loadedLocationMentionIds.add(id)
          } catch {
            /* unresolved labels fall back to a placeholder */
          }
        })
      )
    } finally {
      ids.forEach((id) => {
        locationMentionLoadPromises.delete(id)
        locationMentionLoadResolvers.get(id)?.()
        locationMentionLoadResolvers.delete(id)
      })
    }
  })
}

function loadLocationMention(id: string, controller: LocationController): Promise<void> {
  const existingLoad = locationMentionLoadPromises.get(id)
  if (existingLoad) return existingLoad

  let resolveLoad!: () => void
  const load = new Promise<void>((resolve) => {
    resolveLoad = resolve
  })
  locationMentionLoadPromises.set(id, load)
  locationMentionLoadResolvers.set(id, resolveLoad)
  queuedLocationMentionLoads.add(id)
  locationMentionLoadController ??= controller
  scheduleLocationMentionLoads()

  return load
}

export function isSlashTriggerKey(key: string, keysBefore: string[]): boolean {
  if (key !== LOCATION_TRIGGER) return false
  if (keysBefore.length === 0) return true
  return /\s/.test(keysBefore[keysBefore.length - 1] ?? "")
}

export type LocationTrigger = "slash" | "double-space"

export type KeypressMentionAction = { type: "open"; trigger: LocationTrigger } | { type: "close" }

export function getKeypressMentionAction(
  key: string,
  keysBefore: string[],
  isOpen: boolean
): KeypressMentionAction | null {
  if (key !== " ") {
    if (isSlashTriggerKey(key, keysBefore)) return { type: "open", trigger: "slash" }
    return null
  }

  if (!isDoubleSpaceGesture(pushRecentKey(keysBefore, key))) return null
  if (isOpen) return { type: "close" }
  return { type: "open", trigger: "double-space" }
}

// --- Active mention session detection ---

export type MentionSessionCommand = {
  start: number
  query: string
}

export type PersonMentionCommand = MentionSessionCommand

const TRIGGER_SPACES = new Set([" ", " "])
const FILE_TRIGGER = "+"

function isSpaceChar(value: string | undefined): boolean {
  return value !== undefined && TRIGGER_SPACES.has(value)
}

/** A mention query is valid while it does not start with whitespace or contain newlines. */
export function isValidMentionQuery(query: string): boolean {
  return query.length === 0 || !/^\s|[\r\n]/.test(query)
}

function isTriggerBoundary(value: string, index: number): boolean {
  return index === 0 || /\s/.test(value[index - 1])
}

/** Query text for an active location session started at `start`. */
export function getLocationSessionQuery(value: string, cursor: number, start: number): string {
  return value.slice(start + 1, cursor)
}

/** Active `/query` mention session before the cursor. */
export function getActiveLocationMentionCommand(before: string): PersonMentionCommand | null {
  let searchEnd = before.length
  while (searchEnd > 0) {
    const slash = before.lastIndexOf(LOCATION_TRIGGER, searchEnd - 1)
    if (slash === -1) return null
    if (!isTriggerBoundary(before, slash)) {
      searchEnd = slash
      continue
    }

    const query = before.slice(slash + 1)
    if (!isValidMentionQuery(query)) return null

    return { start: slash, query }
  }
  return null
}

function isPersonMentionTrigger(value: string, index: number): boolean {
  // Require start of text or whitespace before @ so `email@test` does not open person search.
  return index === 0 || /\s/.test(value[index - 1] ?? "")
}

/** Active `@query` mention session before the cursor. */
export function getActivePersonMentionCommand(before: string): PersonMentionCommand | null {
  let searchEnd = before.length
  while (searchEnd > 0) {
    const at = before.lastIndexOf("@", searchEnd - 1)
    if (at === -1) return null
    if (!isPersonMentionTrigger(before, at)) {
      searchEnd = at
      continue
    }

    const query = before.slice(at + 1)
    if (!isValidMentionQuery(query)) return null

    return { start: at, query }
  }
  return null
}

/** True while the `@` trigger at `start` is still present before the cursor. */
export function isActivePersonMentionSession(
  displayText: string,
  cursor: number,
  start: number
): boolean {
  if (displayText[start] !== "@") return false
  if (!isPersonMentionTrigger(displayText, start)) return false

  const command = getActivePersonMentionCommand(displayText.slice(0, cursor))
  return command?.start === start
}

/** True while the location trigger at `start` is still present before the cursor. */
export function isActiveLocationMentionSession(
  displayText: string,
  cursor: number,
  start: number,
  trigger: LocationTrigger
): boolean {
  if (trigger === "slash") {
    if (displayText[start] !== LOCATION_TRIGGER) return false
    if (!isTriggerBoundary(displayText, start)) return false

    const command = getActiveLocationMentionCommand(displayText.slice(0, cursor))
    return command?.start === start
  }

  if (displayText[start] !== LOCATION_TRIGGER) return false
  if (start <= 0 || displayText[start - 1] !== " ") return false

  return isValidMentionQuery(getLocationSessionQuery(displayText, cursor, start))
}

// --- Mention kinds ---

export type MentionKind = "location" | "file" | "person" | "project" | "business"

/** Per-kind DOM dataset key, test id and label fallback ("no data" placeholder). */
const KIND_META = {
  location: {
    data: "locationId",
    testId: "location-mention",
    fallback: "locatie",
    title: "Locatie"
  },
  file: { data: "fileId", testId: "file-mention", fallback: "bestand", title: "Bestand" },
  person: { data: "personId", testId: "person-mention", fallback: "persoon", title: "Persoon" },
  project: { data: "projectId", testId: "project-mention", fallback: "project", title: "Project" },
  business: {
    data: "businessId",
    testId: "business-mention",
    fallback: "bedrijf",
    title: "Bedrijf"
  }
} as const

const MENTION_KINDS = Object.keys(KIND_META) as MentionKind[]

export function mentionTestId(kind: MentionKind): string {
  return KIND_META[kind].testId
}

/** The mention kind encoded on a rendered mention element, if any. */
function mentionKindOf(el: HTMLElement): MentionKind | null {
  return MENTION_KINDS.find((kind) => el.dataset[KIND_META[kind].data]) ?? null
}

const MENTION_ELEMENT_SELECTOR = MENTION_KINDS.map(
  (kind) => `[data-${KIND_META[kind].data.replace(/Id$/, "-id")}]`
).join(",")

export function isMentionEditorEmpty(editor: HTMLElement): boolean {
  return editor.textContent === "" && !editor.querySelector(MENTION_ELEMENT_SELECTOR)
}

// --- Pending location mention UI ---

export const PENDING_LOCATION_MENTION_CLASS = "mention-location-pending"

export const LOCATION_MENTION_HOVER_TITLE = LOCATION_POINTER_HOVER_TITLE

/** Spinner SVG shown on hover while a location mention is still saving. */
export const PENDING_LOCATION_SPINNER_SVG = `<svg class="mention-location-pending-spinner-icon" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="#E5E7EB"/><path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentColor"/></svg>`

function createPendingLocationSpinner(): HTMLElement {
  const spinner = document.createElement("span")
  spinner.className = "mention-location-pending-spinner"
  spinner.setAttribute("aria-hidden", "true")
  spinner.innerHTML = PENDING_LOCATION_SPINNER_SVG
  return spinner
}

// --- @ mention autocomplete ---

const AT_MENTION_AUTOCOMPLETE_MIN_CHARS = 1
const AT_MENTION_AUTOCOMPLETE_LIMIT = 10

const PROJECT_MENTION_PREFIX = "prj"
const BUSINESS_MENTION_PREFIX = "bdr"

export type AtMentionMode = "contact" | "project" | "business"

export type ParsedAtMentionQuery = {
  mode: AtMentionMode
  search: string
}

export type AtMentionSuggestion = {
  key: string
  label: string
  mode: AtMentionMode
}

export function parseAtMentionQuery(query: string): ParsedAtMentionQuery {
  const lower = query.toLowerCase()
  if (lower.startsWith(PROJECT_MENTION_PREFIX)) {
    return {
      mode: "project",
      search: query.slice(PROJECT_MENTION_PREFIX.length).trimStart()
    }
  }
  if (lower.startsWith(BUSINESS_MENTION_PREFIX)) {
    return {
      mode: "business",
      search: query.slice(BUSINESS_MENTION_PREFIX.length).trimStart()
    }
  }
  return { mode: "contact", search: query }
}

const PROJECT_MENTION_SEARCH_FIELDS = [
  { name: "name" },
  { name: "executor.knownBusinessEntity.name" }
] as const

function projectToMentionSuggestion(project: Project): AtMentionSuggestion | null {
  return project.id ? { key: project.id, label: project.getMentionLabel(), mode: "project" } : null
}

const CREATE_MENTION_TRIGGER = "+"

/**
 * Detects a create intent from an `@` query. A trailing single `+` right after
 * the mode prefix (e.g. `@+`, `@prj+`, `@bdr+`) requests creating a new entity
 * of that mode. Returns `null` when the query is a regular search.
 */
export function parseAtMentionCreateIntent(query: string): AtMentionMode | null {
  if (!query.endsWith(CREATE_MENTION_TRIGGER)) return null
  const withoutTrigger = query.slice(0, -CREATE_MENTION_TRIGGER.length)
  const parsed = parseAtMentionQuery(withoutTrigger)
  // Only treat as a create intent when the `+` immediately follows the prefix,
  // i.e. there is no additional search text before it (`@prj foo+` is a search).
  if (parsed.search.length > 0) return null
  return parsed.mode
}

/** Fuzzy-search one suggestion pool (query hit) or list it alphabetically (empty query). */
function searchSuggestionPool<T>(
  query: string,
  pool: T[],
  fields: { name: string }[],
  toSuggestion: (item: T) => AtMentionSuggestion | null,
  limit = AT_MENTION_AUTOCOMPLETE_LIMIT
): AtMentionSuggestion[] {
  const q = query.trim()
  const matches = q
    ? fuzzySearchCollection(pool, fields, q)
    : [...pool].sort((a, b) =>
        (toSuggestion(a)?.label ?? "").localeCompare(toSuggestion(b)?.label ?? "")
      )
  return matches
    .map(toSuggestion)
    .filter((s): s is AtMentionSuggestion => !!s?.key)
    .slice(0, limit)
}

export function searchAtMentionSuggestions(
  query: string,
  contacts: KnownBusinessEntityContact[],
  projects: Project[],
  businesses: KnownBusinessEntity[]
): AtMentionSuggestion[] {
  const parsed = parseAtMentionQuery(query)
  if (parsed.mode === "project") {
    return searchSuggestionPool(
      parsed.search,
      projects,
      [...PROJECT_MENTION_SEARCH_FIELDS],
      projectToMentionSuggestion
    )
  }
  if (parsed.mode === "business") {
    return searchSuggestionPool(parsed.search, businesses, [{ name: "name" }], (business) =>
      business.id ? { key: business.id, label: business.name, mode: "business" } : null
    )
  }

  const contactSuggestions = searchSuggestionPool(
    parsed.search,
    contacts.filter((contact) => contact.isActive),
    [
      { name: "person.firstName" },
      { name: "person.lastName" },
      { name: "knownBusinessEntity.name" }
    ],
    (contact) =>
      contact.id
        ? { key: contact.id, label: contact.getDisplayText().trim(), mode: "contact" }
        : null,
    AT_MENTION_AUTOCOMPLETE_LIMIT
  )
  const projectSuggestions = searchSuggestionPool(
    parsed.search,
    projects,
    [...PROJECT_MENTION_SEARCH_FIELDS],
    projectToMentionSuggestion,
    AT_MENTION_AUTOCOMPLETE_LIMIT
  )
  const businessSuggestions = searchSuggestionPool(
    parsed.search,
    businesses,
    [{ name: "name" }],
    (business) =>
      business.id ? { key: business.id, label: business.name, mode: "business" } : null,
    AT_MENTION_AUTOCOMPLETE_LIMIT
  )

  return [...contactSuggestions, ...projectSuggestions, ...businessSuggestions]
    .sort((a, b) => a.label.localeCompare(b.label))
    .slice(0, AT_MENTION_AUTOCOMPLETE_LIMIT)
}

// --- Core mention logic ---

type BodySegment =
  { type: "text"; value: string } | { type: "mention"; kind: MentionKind; id: string }

const MENTION_TOKEN_RE =
  /\{\{(location|file|person|project|business):((?:pending-location-[0-9a-fA-F-]+)|(?:[0-9a-fA-F-]{8,}))\}\}/g

/** Client-side location IDs awaiting server persistence after optimistic insert. */
const pendingLocationIds = new Set<string>()
const pendingLocationLabels = new Map<string, string>()

export function isPendingLocationId(id: string): boolean {
  return pendingLocationIds.has(id)
}

export function getPendingLocationBaseLabel(id: string): string {
  return pendingLocationLabels.get(id) ?? "locatie"
}

function clearPendingLocation(id: string) {
  pendingLocationIds.delete(id)
  pendingLocationLabels.delete(id)
}

let trackingMapVisible = false

/** Set by {@link SupplierAndUserTrackingMap} when the Kaart column is visible in the viewport. */
export function setTrackingMapVisible(value: boolean) {
  trackingMapVisible = value
}

function mentionLinkClass(kind: MentionKind, interactive = true): string {
  const base =
    kind === "location"
      ? "font-bold underline underline-offset-2"
      : kind === "person" || kind === "project" || kind === "business"
        ? "font-bold text-primary dark:text-primary-dark underline underline-offset-2"
        : "font-bold text-info underline decoration-solid underline-offset-2"
  if (!interactive) {
    if (kind === "person" || kind === "project" || kind === "business")
      return "font-bold text-primary dark:text-primary-dark"
    if (kind === "location") return "font-bold"
    return "font-bold text-info dark:text-info-dark"
  }
  if (kind === "file") return `${base} cursor-pointer hover:text-info/80`
  return `${base} cursor-pointer`
}

export function getFileTriggerAt(value: string, cursor: number): number | null {
  const i = cursor - 1
  if (i < 1 || value[i] !== FILE_TRIGGER || value[i - 1] !== FILE_TRIGGER) return null
  if (!isTriggerBoundary(value, i - 1)) return null
  return i - 1
}

function parseBody(body: string): BodySegment[] {
  const segments: BodySegment[] = []
  let last = 0
  let match: RegExpExecArray | null
  MENTION_TOKEN_RE.lastIndex = 0
  while ((match = MENTION_TOKEN_RE.exec(body)) !== null) {
    if (match.index > last) segments.push({ type: "text", value: body.slice(last, match.index) })
    segments.push({ type: "mention", kind: match[1] as MentionKind, id: match[2] })
    last = match.index + match[0].length
  }
  if (last < body.length) segments.push({ type: "text", value: body.slice(last) })
  return segments
}

export function formatMentionText(
  body: string,
  labelFor: (kind: MentionKind, id: string) => string
): string {
  return parseBody(body)
    .map((segment) =>
      segment.type === "mention" ? labelFor(segment.kind, segment.id) : segment.value
    )
    .join("")
}

function displayToBodyOffset(
  body: string,
  displayOffset: number,
  labelFor: (kind: MentionKind, id: string) => string
): number {
  let displayPos = 0
  let last = 0
  let match: RegExpExecArray | null
  MENTION_TOKEN_RE.lastIndex = 0
  while ((match = MENTION_TOKEN_RE.exec(body)) !== null) {
    const textLen = match.index - last
    if (displayOffset <= displayPos + textLen) return last + (displayOffset - displayPos)
    displayPos += textLen

    const nameLen = labelFor(match[1] as MentionKind, match[2]).length
    const tokenStart = match.index
    const tokenEnd = match.index + match[0].length
    if (displayOffset < displayPos + nameLen) {
      return displayOffset - displayPos <= nameLen / 2 ? tokenStart : tokenEnd
    }
    if (displayOffset === displayPos + nameLen) return tokenEnd
    displayPos += nameLen
    last = tokenEnd
  }
  return last + Math.max(0, displayOffset - displayPos)
}

/** A resolvable mention entity (everything except files). */
type MentionEntityKind = Exclude<MentionKind, "file">
type MentionEntity = Location | KnownBusinessEntityContact | Project | KnownBusinessEntity

export function formatLocationMentionLabel(
  location: { name?: string | null; address?: string | null } | null | undefined,
  options?: { preferAddress?: boolean; nameWithAddress?: boolean }
): string {
  const name = location?.name?.trim()
  const address = location?.address?.trim()

  if (options?.nameWithAddress) {
    if (name && address) return stripCountryFromLocationText(`${name}: ${address}`)
    const fallback = address ?? name ?? "locatie"
    return fallback === "locatie" ? fallback : stripCountryFromLocationText(fallback)
  }

  const raw = options?.preferAddress ? (address ?? name ?? "locatie") : name || address || "locatie"
  return raw === "locatie" ? raw : stripCountryFromLocationText(raw)
}

/**
 * Shared inline-mention logic (PNTS-2917): resolve `{{location:<uuid>}}` and
 * `{{file:<uuid>}}` references — a location's live name + map modal, and a
 * file's type-aware label + viewer.
 */
export function useMention(options?: {
  preferLocationAddress?: boolean
  /** Show `locatienaam: adres` when both are available (e.g. instruction details). */
  locationNameWithAddress?: boolean
  /** Read-only location sheet (e.g. app instruction details). */
  useLocationDetailModal?: boolean
}) {
  const controller = inject<LocationController>("locationController")
  const { tenantBaseLocations } = storeToRefs(useLocationStore())
  const { activeKnownBusinessEntitiesContacts, knownBusinessEntitiesContacts } = storeToRefs(
    useKnownBusinessEntitiesContactsStore()
  )
  const { projects } = storeToRefs(useProjectStore())
  const { knownBusinessEntities } = storeToRefs(useKnownBusinessEntityStore())
  const preferLocationAddress = options?.preferLocationAddress ?? false
  const locationNameWithAddress = options?.locationNameWithAddress ?? false

  const locationLabelsRevision = ref(0)
  watch(
    tenantBaseLocations,
    () => {
      locationLabelsRevision.value++
    },
    { deep: true }
  )

  const mapLocation = ref<Location | null>(null)
  const mapAnchor = ref<{ x: number; y: number } | null>(null)
  const { activeUser } = storeToRefs(useAuthStore())
  const modelFormStore = useModelFormStore()

  function shouldUseLocationDetailModal(): boolean {
    if (options?.useLocationDetailModal) return true
    return shouldUseReadOnlyMentionDetailModal(activeUser.value?.knownBusinessEntityContact)
  }

  function useReadOnlyMentionDetailModal(): boolean {
    return shouldUseReadOnlyMentionDetailModal(activeUser.value?.knownBusinessEntityContact)
  }

  function usesInlineLocationMap(): boolean {
    if (trackingMapVisible) return false
    return !shouldUseLocationDetailModal()
  }

  const managed = <T>(id: string) => ModelManager.getInstance().get(id) as T | undefined

  function getContact(id: string): KnownBusinessEntityContact | null {
    return (
      managed<KnownBusinessEntityContact>(id) ??
      knownBusinessEntitiesContacts.value.find((contact) => contact.id === id) ??
      activeKnownBusinessEntitiesContacts.value.find((contact) => contact.id === id) ??
      knownBusinessEntitiesContacts.value.find((contact) => contact.person?.id === id) ??
      activeKnownBusinessEntitiesContacts.value.find((contact) => contact.person?.id === id) ??
      null
    )
  }

  /** Resolve a mention's entity from the model manager or the matching store. */
  function resolveEntity(kind: MentionEntityKind, id: string): MentionEntity | null {
    if (kind === "person") return getContact(id)
    if (kind === "project")
      return managed<Project>(id) ?? projects.value.find((p) => p.id === id) ?? null
    if (kind === "business")
      return (
        managed<KnownBusinessEntity>(id) ??
        knownBusinessEntities.value.find((b) => b.id === id) ??
        null
      )
    return managed<Location>(id) ?? tenantBaseLocations.value.find((l) => l.id === id) ?? null
  }

  function locationLabel(id: string): string {
    if (isPendingLocationId(id)) {
      return stripCountryFromLocationText(getPendingLocationBaseLabel(id))
    }
    const location = resolveEntity("location", id) as Location | null
    return formatLocationMentionLabel(location, {
      preferAddress: preferLocationAddress,
      nameWithAddress: locationNameWithAddress
    })
  }

  function fileLabel(id: string): string {
    const file = managed<FileRelation>(id)?.file
    if (!file) return KIND_META.file.fallback
    if (file.isImage()) return "afbeelding"
    if (file.extension?.toLowerCase() === "pdf") return "pdf"
    if (file.isEmail()) return "e-mail"
    if (file.isDocument()) return "document"
    return KIND_META.file.fallback
  }

  function labelFor(kind: MentionKind, id: string): string {
    if (kind === "file") return fileLabel(id)
    if (kind === "location") return locationLabel(id)
    const entity = resolveEntity(kind, id)
    const name =
      kind === "person"
        ? (entity as KnownBusinessEntityContact | null)?.getDisplayText().trim()
        : kind === "project"
          ? (entity as Project | null)?.getMentionLabel().trim()
          : (entity as KnownBusinessEntity | null)?.name?.trim()
    return name || KIND_META[kind].fallback
  }

  /** Hover title for a rendered mention ("Open <label>", or the kind when unresolved). */
  function mentionTitle(kind: MentionKind, id: string): string {
    if (kind === "file") return `Open ${fileLabel(id)}`
    if (kind === "location") return LOCATION_MENTION_HOVER_TITLE
    const label = labelFor(kind, id)
    if (label === KIND_META[kind].fallback) return KIND_META[kind].title
    return `Open ${label}`
  }

  async function ensureLoaded(id: string): Promise<void> {
    if (isPendingLocationId(id)) return
    const location = resolveEntity("location", id) as Location | null
    const needsFetch =
      !location || location.name == null || location.latitude == null || location.longitude == null
    if (!needsFetch || loadedLocationMentionIds.has(id) || !controller) return
    await loadLocationMention(id, controller)
  }

  async function ensureBodyMentionsLoaded(body: string): Promise<void> {
    const locationIds = parseBody(body).flatMap((segment) =>
      segment.type === "mention" && segment.kind === "location" ? [segment.id] : []
    )
    await Promise.all(locationIds.map(ensureLoaded))
  }

  function formatBodyForDisplay(body: string): string {
    return formatMentionText(body, labelFor)
  }

  function clearLocationMentionFocus(id?: string) {
    Bus.emit("location-mention-focus-clear", { id })
  }

  function emitLocationFocus(location: Location) {
    if (location.id && location.latitude != null && location.longitude != null) {
      Bus.emit("location-mention-focus", {
        id: location.id,
        latitude: location.latitude,
        longitude: location.longitude,
        name: location.name,
        address: location.address
      })
    }
  }

  function openMap(location: Location, event?: Event) {
    emitLocationFocus(location)
    if (trackingMapVisible) return
    mapAnchor.value = event instanceof MouseEvent ? { x: event.clientX, y: event.clientY } : null
    mapLocation.value = location
  }

  async function openMapById(id: string, event?: Event) {
    if (isPendingLocationId(id)) return

    if (mapLocation.value?.id === id) {
      closeMap()
      clearLocationMentionFocus(id)
      return
    }

    if (mentionDetail.value?.kind === "location" && mentionDetail.value.id === id) {
      closeMentionDetail()
      clearLocationMentionFocus(id)
      return
    }

    await ensureLoaded(id)
    const location = resolveEntity("location", id) as Location | null
    if (!location) return
    if (shouldUseLocationDetailModal()) {
      emitLocationFocus(location)
      openMentionDetail({ kind: "location", id })
      return
    }
    openMap(location, event)
  }

  function closeMap() {
    mapLocation.value = null
  }

  async function refreshOpenMapLocation() {
    const id = mapLocation.value?.id
    if (!id || !controller) return
    try {
      const location = await controller.detail(id)
      if (location) mapLocation.value = location
    } catch {
      /* keep showing the last known location */
    }
  }

  function openFile(id: string) {
    const fileRelation = managed<FileRelation>(id)
    if (fileRelation) void FileViewerController.display([fileRelation], fileRelation)
  }

  async function openLocationModalById(id: string, event?: Event) {
    if (isPendingLocationId(id)) return

    await ensureLoaded(id)
    const location = resolveEntity("location", id) as Location | null
    if (!location) return

    if (shouldUseLocationDetailModal()) {
      emitLocationFocus(location)
      openMentionDetail({ kind: "location", id })
      return
    }

    emitLocationFocus(location)
    if (trackingMapVisible) {
      Bus.emit("location-map-modal-open", { location })
      return
    }

    mapAnchor.value = event instanceof MouseEvent ? { x: event.clientX, y: event.clientY } : null
    mapLocation.value = location
  }

  let pendingLocationClick: {
    id: string
    event: Event
    timer: ReturnType<typeof setTimeout>
  } | null = null

  function handleLocationMentionClick(id: string, event: Event) {
    if (pendingLocationClick) clearTimeout(pendingLocationClick.timer)
    pendingLocationClick = {
      id,
      event,
      timer: setTimeout(() => {
        const pending = pendingLocationClick
        pendingLocationClick = null
        if (pending) void openMapById(pending.id, pending.event)
      }, LOCATION_MENTION_CLICK_DELAY_MS)
    }
  }

  function handleLocationMentionDoubleClick(id: string, event: Event) {
    if (pendingLocationClick) {
      clearTimeout(pendingLocationClick.timer)
      pendingLocationClick = null
    }
    event.preventDefault()
    event.stopPropagation()
    void openLocationModalById(id, event)
  }

  /** Open the detail/edit UI for any mention kind. */
  function openMention(kind: MentionKind, id: string, event?: Event) {
    if (kind === "location") return void openMapById(id, event)
    if (kind === "file") return openFile(id)

    const entity = resolveEntity(kind, id)
    if (!entity) return
    if (useReadOnlyMentionDetailModal()) {
      openMentionDetail({ kind, id })
      return
    }
    if (kind === "person") {
      const contact = entity as KnownBusinessEntityContact
      modelFormStore.openForm({
        model: contact.person,
        creationParameters: { focusRoleButton: contact }
      })
    } else {
      modelFormStore.openForm({ model: entity })
    }
  }

  return {
    parseBody,
    mentionLinkClass,
    labelFor,
    mentionTitle,
    resolveEntity,
    ensureLoaded,
    ensureBodyMentionsLoaded,
    formatBodyForDisplay,
    mapLocation,
    mapAnchor,
    openMention,
    openMapById,
    openLocationModalById,
    handleLocationMentionClick,
    handleLocationMentionDoubleClick,
    closeMap,
    refreshOpenMapLocation,
    fileLabel,
    openFile,
    usesInlineLocationMap,
    locationLabelsRevision
  }
}

type PendingAtMentionCreate = {
  offset: number
  mode: AtMentionMode
  entityId: string
}

type SelectorMode = "location" | "at"

/** Per-@-mode mention kind, create-form model class and saved-model matcher. */
const MODE_META = {
  contact: {
    kind: "person" as const,
    createModel: () => ModelFactory.createModel(Person),
    savedClassName: () => KnownBusinessEntityContact.className,
    fallback: "persoon"
  },
  project: {
    kind: "project" as const,
    createModel: () => ModelFactory.createModel(Project),
    savedClassName: () => Project.className,
    fallback: "project"
  },
  business: {
    kind: "business" as const,
    createModel: () => ModelFactory.createModel(KnownBusinessEntity),
    savedClassName: () => KnownBusinessEntity.className,
    fallback: "bedrijf"
  }
}

/** Contenteditable editor with location autocomplete and file mentions. */
export function useMentionEditor(options: {
  text: Ref<string | null | undefined>
  autoFocus?: boolean
  transformClipboard?: (value: string) => string
  onInput: (event: Event) => void
  onFileTrigger: () => void
}) {
  const MIN_CHARS = 3
  const locationController = inject<LocationController>("locationController")
  const { activeUser } = storeToRefs(useAuthStore())
  const { tenantBaseLocations } = storeToRefs(useLocationStore())
  const { activeKnownBusinessEntitiesContacts, knownBusinessEntitiesContacts } = storeToRefs(
    useKnownBusinessEntitiesContactsStore()
  )
  const { projects } = storeToRefs(useProjectStore())
  const { knownBusinessEntities } = storeToRefs(useKnownBusinessEntityStore())
  const mention = useMention()
  const modelFormStore = useModelFormStore()

  const rootEl = ref<HTMLElement>()
  const editorEl = ref<HTMLElement>()
  const isFocused = ref(false)
  const { open, loading, items, highlightedIndex, query, trimmedQuery } = useSuggestionList()
  const atItems = ref<AtMentionSuggestion[]>([])
  const atMentionMode = ref<AtMentionMode>("contact")
  const selectorMode = ref<SelectorMode | null>(null)
  const selectorTop = ref(0)
  const pendingFileOffset = ref<number | null>(null)
  const pendingAtMentionCreate = ref<PendingAtMentionCreate | null>(null)
  const slashStart = ref<number | null>(null)
  const slashEnd = ref<number | null>(null)
  const locationTrigger = ref<LocationTrigger | null>(null)
  const recentKeys = ref<string[]>([])
  const pendingMentionAction = ref<KeypressMentionAction | null>(null)

  const delayedSearch = useDelayedFunction(runSearch, 250)

  onClickOutside(rootEl, () => {
    if (open.value) closeMenu()
  })

  onMounted(() => {
    renderEditor(options.text.value ?? "")
    if (options.autoFocus) nextTick(focus)
  })

  watch(options.text, (value) => {
    if (!isFocused.value) renderEditor(value ?? "")
  })
  watch(
    tenantBaseLocations,
    () => {
      if (!isFocused.value) {
        renderEditor(options.text.value ?? "")
        return
      }
      refreshLocationMentionLabels()
    },
    { deep: true }
  )

  function refreshLocationMentionLabels() {
    if (!editorEl.value) return
    editorEl.value.querySelectorAll("[data-location-id]").forEach((node) => {
      const el = node as HTMLElement
      const id = el.dataset.locationId
      if (!id || isPendingLocationId(id) || el.dataset.saving === "true") return
      const label = mention.labelFor("location", id)
      if (el.textContent !== label) el.textContent = label
      el.title = LOCATION_MENTION_HOVER_TITLE
    })
  }

  function refreshLocationMentionLabel(id: string) {
    if (isPendingLocationId(id)) return
    const label = mention.labelFor("location", id)
    editorEl.value?.querySelectorAll(`[data-location-id="${id}"]`).forEach((node) => {
      const el = node as HTMLElement
      if (el.dataset.saving === "true") return
      if (el.textContent !== label) el.textContent = label
      el.title = LOCATION_MENTION_HOVER_TITLE
    })
  }

  function walkNode(node: ChildNode, format: "body" | "display"): string {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? ""
    if (!(node instanceof HTMLElement)) return ""
    const kind = mentionKindOf(node)
    if (kind) {
      return format === "body"
        ? `{{${kind}:${node.dataset[KIND_META[kind].data]}}}`
        : (node.textContent ?? "")
    }
    if (node.tagName === "BR") return "\n"
    const childText = [...node.childNodes].map((n) => walkNode(n, format)).join("")
    return node.tagName === "DIV" || node.tagName === "P" ? `${childText}\n` : childText
  }

  function walkEditor(format: "body" | "display"): string {
    if (!editorEl.value) return ""
    if (isMentionEditorEmpty(editorEl.value)) return ""
    return [...editorEl.value.childNodes].map((n) => walkNode(n, format)).join("")
  }

  function ensureLoaded(id: string) {
    if (isPendingLocationId(id)) return
    void mention.ensureLoaded(id).then(() => refreshLocationMentionLabel(id))
  }

  function createMentionElement(kind: MentionKind, id: string): HTMLElement {
    const span = document.createElement("span")
    span.contentEditable = "false"
    span.className = mentionLinkClass(kind)
    span.dataset[KIND_META[kind].data] = id
    span.setAttribute("data-testid", KIND_META[kind].testId)

    if (kind === "location" && isPendingLocationId(id)) {
      span.dataset.saving = "true"
      span.className = `${mentionLinkClass("location", false)} ${PENDING_LOCATION_MENTION_CLASS}`
      span.title = "Locatie wordt opgeslagen…"
      span.append(document.createTextNode(getPendingLocationBaseLabel(id)))
      span.append(createPendingLocationSpinner())
      return span
    }

    if (kind === "location") {
      ensureLoaded(id)
      span.title = LOCATION_MENTION_HOVER_TITLE
    }
    span.textContent = mention.labelFor(kind, id)
    return span
  }

  function renderEditor(body: string) {
    if (!editorEl.value) return
    editorEl.value.replaceChildren()
    for (const segment of parseBody(body)) {
      editorEl.value.append(
        segment.type === "text"
          ? document.createTextNode(segment.value)
          : createMentionElement(segment.kind, segment.id)
      )
    }
  }

  function getCaretOffset(): number {
    if (!editorEl.value) return 0
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return walkEditor("display").length
    const range = selection.getRangeAt(0).cloneRange()
    range.selectNodeContents(editorEl.value)
    range.setEnd(selection.anchorNode ?? editorEl.value, selection.anchorOffset)
    return range.toString().length
  }

  function setCaretByDisplayOffset(offset: number) {
    const el = editorEl.value
    if (!el) return
    const range = document.createRange()
    const selection = window.getSelection()
    let position = 0
    for (const node of el.childNodes) {
      const nodeLength = walkNode(node, "display").length
      if (node.nodeType === Node.TEXT_NODE && offset <= position + nodeLength) {
        range.setStart(node, Math.max(0, offset - position))
        range.collapse(true)
        selection?.removeAllRanges()
        selection?.addRange(range)
        return
      }
      if (
        node instanceof HTMLElement &&
        (node.dataset.locationId || node.dataset.fileId || node.dataset.personId)
      ) {
        if (offset <= position) range.setStartBefore(node)
        else if (offset <= position + nodeLength) range.setStartAfter(node)
        else {
          position += nodeLength
          continue
        }
        range.collapse(true)
        selection?.removeAllRanges()
        selection?.addRange(range)
        return
      }
      position += nodeLength
    }
    range.selectNodeContents(el)
    range.collapse(false)
    selection?.removeAllRanges()
    selection?.addRange(range)
  }

  function getEditorBody(): string {
    if (!isFocused.value) return options.text.value ?? ""
    return walkEditor("body")
  }

  function replaceDisplayRange(displayStart: number, displayEnd: number, replacement: string) {
    const body = getEditorBody()
    const bodyStart = displayToBodyOffset(body, displayStart, mention.labelFor)
    const bodyEnd = displayToBodyOffset(body, displayEnd, mention.labelFor)
    const next = body.slice(0, bodyStart) + replacement + body.slice(bodyEnd)
    options.text.value = next
    renderEditor(next)
    return next
  }

  const mentionTrailingSpace = " "

  function caretAfterMention(replaceStart: number, displayLabelLength: number): number {
    return replaceStart + displayLabelLength + mentionTrailingSpace.length
  }

  function insertTextAtSelection(value: string) {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return
    const range = selection.getRangeAt(0)
    range.deleteContents()
    const node = document.createTextNode(value)
    range.insertNode(node)
    range.setStart(node, value.length)
    range.collapse(true)
    selection.removeAllRanges()
    selection.addRange(range)
  }

  function revertDoubleSpaceSlashIfNeeded() {
    const trigger = locationTrigger.value
    const slashPos = slashStart.value
    if (trigger !== "double-space" || slashPos == null || !editorEl.value) return
    if (query.value.trim().length > 0) return

    const displayText = walkEditor("display")
    if (displayText[slashPos] !== LOCATION_TRIGGER) return
    if (slashPos <= 0 || displayText[slashPos - 1] !== " ") return

    replaceDisplayRange(slashPos, slashPos + 1, " ")
    setCaretByDisplayOffset(slashPos + 1)
  }

  function closeMenu() {
    revertDoubleSpaceSlashIfNeeded()
    open.value = false
    items.value = []
    query.value = ""
    highlightedIndex.value = 0
    slashStart.value = null
    slashEnd.value = null
    locationTrigger.value = null
    selectorMode.value = null
    atMentionMode.value = "contact"
    atItems.value = []
    pendingMentionAction.value = null
    delayedSearch.cancel()
  }

  function trackKeypress(event: KeyboardEvent) {
    const key = event.key
    if (shouldClearRecentKeys(key)) {
      recentKeys.value = []
      return
    }
    if (event.ctrlKey || event.metaKey || event.altKey || key.length !== 1) return

    const keysBefore = recentKeys.value
    const action = getKeypressMentionAction(
      key,
      keysBefore,
      open.value && (selectorMode.value === "location" || selectorMode.value === "at")
    )
    recentKeys.value = pushRecentKey(keysBefore, key)
    if (action) pendingMentionAction.value = action
  }

  function openLocationSession(displayText: string, cursor: number, trigger: LocationTrigger) {
    const start = cursor - 1
    selectorMode.value = "location"
    locationTrigger.value = trigger
    slashStart.value = start
    slashEnd.value = cursor
    query.value = getLocationSessionQuery(displayText, cursor, start)
    open.value = true
    updateSelectorPosition()
    delayedSearch.schedule()
  }

  /** Turn a double-space gesture into ` /` and open location search on the slash. */
  function openLocationSessionFromDoubleSpace(cursor: number) {
    const replaceStart = cursor - 2
    replaceDisplayRange(replaceStart, cursor, " /")
    const newCursor = replaceStart + 2

    selectorMode.value = "location"
    locationTrigger.value = "double-space"
    slashStart.value = replaceStart + 1
    slashEnd.value = newCursor
    query.value = ""
    open.value = true
    setCaretByDisplayOffset(newCursor)
    updateSelectorPosition()
    delayedSearch.schedule()
  }

  function closeMentionSessionByDoubleSpace(displayText: string, cursor: number) {
    closeMenu()
    if (cursor > 0 && isSpaceChar(displayText[cursor - 1])) {
      replaceDisplayRange(cursor - 1, cursor, "")
      setCaretByDisplayOffset(cursor - 1)
    }
  }

  function updateSelectorPosition() {
    const el = editorEl.value
    const root = rootEl.value
    if (!el || !root) return
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return
    const rect = selection.getRangeAt(0).getBoundingClientRect()
    const rootRect = root.getBoundingClientRect()
    selectorTop.value = (rect.bottom || rootRect.top) - rootRect.top + 4
  }

  /** Refresh an open (or newly-detected) session's query + selector, and re-search. */
  function continueSession(
    mode: SelectorMode,
    start: number,
    cursor: number,
    sessionQuery: string
  ) {
    selectorMode.value = mode
    slashStart.value = start
    slashEnd.value = cursor
    query.value = sessionQuery
    if (mode === "at") {
      locationTrigger.value = null
      atMentionMode.value = parseAtMentionQuery(sessionQuery).mode
    }
    open.value = true
    updateSelectorPosition()
    delayedSearch.schedule()
  }

  function updateMentionState(displayText: string, cursor: number) {
    const before = displayText.slice(0, cursor)
    const pending = pendingMentionAction.value
    if (pending) {
      pendingMentionAction.value = null
      if (pending.type === "close") {
        closeMentionSessionByDoubleSpace(displayText, cursor)
        return
      }
      if (pending.trigger === "double-space") {
        openLocationSessionFromDoubleSpace(cursor)
        return
      }
      openLocationSession(displayText, cursor, pending.trigger)
      return
    }

    const personCommand = getActivePersonMentionCommand(before)
    const locationCommand = getActiveLocationMentionCommand(before)
    const activeCommand =
      personCommand && locationCommand
        ? personCommand.start >= locationCommand.start
          ? personCommand
          : locationCommand
        : (personCommand ?? locationCommand)

    if (activeCommand === personCommand && personCommand) {
      const createMode = parseAtMentionCreateIntent(personCommand.query)
      if (createMode) {
        triggerAtMentionCreate(personCommand.start, cursor, createMode)
        return
      }
      continueSession("at", personCommand.start, cursor, personCommand.query)
      return
    }

    if (activeCommand === locationCommand && locationCommand) {
      locationTrigger.value = "slash"
      continueSession("location", locationCommand.start, cursor, locationCommand.query)
      return
    }

    if (open.value && selectorMode.value === "at" && slashStart.value != null) {
      if (!isActivePersonMentionSession(displayText, cursor, slashStart.value)) {
        closeMenu()
        return
      }
      const sessionQuery = displayText.slice(slashStart.value + 1, cursor)
      if (!isValidMentionQuery(sessionQuery)) {
        closeMenu()
        return
      }
      const createMode = parseAtMentionCreateIntent(sessionQuery)
      if (createMode) {
        triggerAtMentionCreate(slashStart.value, cursor, createMode)
        return
      }
      continueSession("at", slashStart.value, cursor, sessionQuery)
      return
    }

    if (open.value && locationTrigger.value && slashStart.value != null) {
      if (
        !isActiveLocationMentionSession(
          displayText,
          cursor,
          slashStart.value,
          locationTrigger.value
        )
      ) {
        closeMenu()
        return
      }
      const sessionQuery = getLocationSessionQuery(displayText, cursor, slashStart.value)
      if (!isValidMentionQuery(sessionQuery)) {
        closeMenu()
        return
      }
      continueSession("location", slashStart.value, cursor, sessionQuery)
      return
    }

    if (open.value) closeMenu()
  }

  function handleInput(event: Event) {
    if (!editorEl.value) {
      closeMenu()
      return
    }
    const displayText = walkEditor("display")
    const cursor = getCaretOffset()

    const fileTrigger = getFileTriggerAt(displayText, cursor)
    if (fileTrigger != null) {
      replaceDisplayRange(fileTrigger, fileTrigger + 2, "")
      setCaretByDisplayOffset(fileTrigger)
      pendingFileOffset.value = fileTrigger
      closeMenu()
      options.text.value = walkEditor("body")
      options.onInput(event)
      options.onFileTrigger()
      return
    }

    updateMentionState(displayText, cursor)

    tryApplyFirstSuggestionStreet(walkEditor("display"), getCaretOffset())

    options.text.value = walkEditor("body")
    options.onInput(event)
  }

  function tryApplyFirstSuggestionStreet(displayText: string, cursor: number) {
    if (!open.value || selectorMode.value !== "location" || slashStart.value == null) return
    if (items.value.length === 0) return

    const lastKey = recentKeys.value.at(-1)
    if (!lastKey || !/^\d$/.test(lastKey)) return

    const sessionQuery = getLocationSessionQuery(displayText, cursor, slashStart.value)
    const queryBeforeLastKey = sessionQuery.slice(0, -lastKey.length)
    if (!shouldApplyFirstSuggestionStreet(sessionQuery, queryBeforeLastKey)) return

    const highlighted = items.value[highlightedIndex.value] ?? items.value[0]
    if (!highlighted) return

    const newSessionQuery = mergeFirstSuggestionStreetWithHouseNumber(sessionQuery, highlighted)
    if (!newSessionQuery || newSessionQuery === sessionQuery) return

    const replaceStart = slashStart.value + 1
    const newCursor = replaceStart + newSessionQuery.length
    replaceDisplayRange(replaceStart, cursor, newSessionQuery)
    setCaretByDisplayOffset(newCursor)
    query.value = newSessionQuery
    slashEnd.value = newCursor
    delayedSearch.schedule()
  }

  function handleKeydown(event: KeyboardEvent) {
    trackKeypress(event)

    if (event.key === "Enter" && !open.value) {
      event.preventDefault()
      insertTextAtSelection("\n")
      handleInput(new Event("input"))
      return
    }
    if (!open.value) return

    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp": {
        event.preventDefault()
        const delta = event.key === "ArrowDown" ? 1 : -1
        const count = (selectorMode.value === "at" ? atItems : items).value.length
        if (count) highlightedIndex.value = (highlightedIndex.value + delta + count) % count
        break
      }
      case "Escape":
        event.preventDefault()
        closeMenu()
        break
      case "Enter":
      case "Tab":
        event.preventDefault()
        void selectHighlightedOrFirstSuggestion()
        break
    }
  }

  function handlePaste(event: ClipboardEvent) {
    event.preventDefault()
    recentKeys.value = []
    const pasted = event.clipboardData?.getData("text/plain") ?? ""
    insertTextAtSelection(options.transformClipboard?.(pasted) ?? pasted)
    handleInput(new Event("input"))
  }

  function handleEditorClick(event: MouseEvent) {
    const el = (event.target as HTMLElement)?.closest?.(
      MENTION_ELEMENT_SELECTOR
    ) as HTMLElement | null
    if (!el) return
    event.preventDefault()
    const kind = mentionKindOf(el)
    if (!kind) return
    const id = el.dataset[KIND_META[kind].data]!
    if (kind === "location" && (el.dataset.saving === "true" || isPendingLocationId(id))) return
    if (kind === "location") {
      mention.handleLocationMentionClick(id, event)
      return
    }
    mention.openMention(kind, id, event)
  }

  function handleEditorDblClick(event: MouseEvent) {
    const el = (event.target as HTMLElement)?.closest?.(
      MENTION_ELEMENT_SELECTOR
    ) as HTMLElement | null
    if (!el) return
    const kind = mentionKindOf(el)
    if (kind !== "location") return
    const id = el.dataset[KIND_META[kind].data]!
    if (el.dataset.saving === "true" || isPendingLocationId(id)) return
    mention.handleLocationMentionDoubleClick(id, event)
  }

  function handleFocusout() {
    window.setTimeout(() => {
      if (!rootEl.value?.contains(document.activeElement)) {
        isFocused.value = false
        recentKeys.value = []
        if (open.value) closeMenu()
      }
    })
  }

  async function runSearch(): Promise<LocationSuggestion[] | AtMentionSuggestion[]> {
    if (selectorMode.value === "at") {
      return runAtSearch()
    }

    const q = trimmedQuery.value
    if (q.length < MIN_CHARS) {
      items.value = []
      loading.value = false
      return []
    }
    loading.value = true
    const results = await searchLocationSuggestions(q, {
      locationController,
      apiKey: activeUser.value?.activeTenant?.tenantSettings?.apiKeyGoogleMaps ?? ""
    })
    if (trimmedQuery.value !== q || !open.value) return []
    items.value = results
    atItems.value = []
    highlightedIndex.value = 0
    loading.value = false
    return results
  }

  async function runAtSearch(): Promise<AtMentionSuggestion[]> {
    const q = trimmedQuery.value
    loading.value = true
    atMentionMode.value = parseAtMentionQuery(q).mode
    const results = searchAtMentionSuggestions(
      q,
      activeKnownBusinessEntitiesContacts.value,
      projects.value,
      knownBusinessEntities.value
    )
    if (trimmedQuery.value !== q || !open.value || selectorMode.value !== "at") return []
    atItems.value = results
    items.value = []
    highlightedIndex.value = 0
    loading.value = false
    return results
  }

  function insertAtMentionAtRange(replaceStart: number, end: number, item: AtMentionSuggestion) {
    const kind = MODE_META[item.mode].kind
    const labelLength = mention.labelFor(kind, item.key).length
    const nextBody = replaceDisplayRange(
      replaceStart,
      end,
      `{{${kind}:${item.key}}}${mentionTrailingSpace}`
    )
    setCaretByDisplayOffset(caretAfterMention(replaceStart, labelLength))
    options.text.value = isFocused.value ? walkEditor("body") : nextBody
    options.onInput(new Event("input"))
  }

  function findContactForPerson(personId: string): KnownBusinessEntityContact | null {
    return (
      knownBusinessEntitiesContacts.value.find((contact) => contact.person?.id === personId) ??
      activeKnownBusinessEntitiesContacts.value.find(
        (contact) => contact.person?.id === personId
      ) ??
      null
    )
  }

  /** Suggestion for a just-created entity: the saved model when it matches, else a lookup. */
  function resolveAtMentionSuggestion(
    pending: PendingAtMentionCreate,
    savedModel?: BaseModel
  ): AtMentionSuggestion | null {
    const meta = MODE_META[pending.mode]
    if (savedModel && savedModel.getClassName() === meta.savedClassName()) {
      if (!savedModel.id) return null
      const label =
        pending.mode === "contact"
          ? (savedModel as KnownBusinessEntityContact).getDisplayText().trim()
          : pending.mode === "project"
            ? (savedModel as Project).getMentionLabel().trim()
            : (savedModel as KnownBusinessEntity).name?.trim() || meta.fallback
      return { key: savedModel.id, label, mode: pending.mode }
    }

    if (pending.mode === "contact") {
      const contact = findContactForPerson(pending.entityId)
      if (!contact?.id) return null
      return { key: contact.id, label: contact.getDisplayText().trim(), mode: "contact" }
    }

    const entity =
      (ModelManager.getInstance().get(pending.entityId) as
        Project | KnownBusinessEntity | undefined) ??
      (pending.mode === "project"
        ? projects.value.find((item) => item.id === pending.entityId)
        : knownBusinessEntities.value.find((item) => item.id === pending.entityId))
    return {
      key: pending.entityId,
      label:
        pending.mode === "project"
          ? (entity as Project | undefined)?.getMentionLabel().trim() || meta.fallback
          : (entity as KnownBusinessEntity | undefined)?.name?.trim() || meta.fallback,
      mode: pending.mode
    }
  }

  function insertCreatedAtMention(pending: PendingAtMentionCreate, savedModel?: BaseModel) {
    const item = resolveAtMentionSuggestion(pending, savedModel)
    if (!item?.key) return

    pendingAtMentionCreate.value = null
    const { offset } = pending

    // Defer until after the model form unmounts so the editor DOM is stable again.
    nextTick(() => {
      requestAnimationFrame(() => {
        insertAtMentionAtRange(offset, offset, item)
        nextTick(focus)
      })
    })
  }

  function openCreateFormForAtMention(mode: AtMentionMode, offset: number) {
    const model = MODE_META[mode].createModel()
    if (!model.id) return

    const pending: PendingAtMentionCreate = { offset, mode, entityId: model.id }
    pendingAtMentionCreate.value = pending
    let savedForInsert: { pending: PendingAtMentionCreate; savedModel?: BaseModel } | null = null

    modelFormStore.openForm({
      model,
      saveHandle: (savedModel?: BaseModel) => {
        savedForInsert = { pending, savedModel }
      },
      closeHandle: () => {
        if (savedForInsert) {
          const { pending: insertPending, savedModel } = savedForInsert
          savedForInsert = null
          insertCreatedAtMention(insertPending, savedModel)
          return
        }
        if (pendingAtMentionCreate.value === pending) {
          pendingAtMentionCreate.value = null
        }
        nextTick(focus)
      }
    })
  }

  function triggerAtMentionCreate(start: number, end: number, mode: AtMentionMode) {
    const displayText = walkEditor("display")
    const replaceEnd = Math.min(end, displayText.length)
    closeMenu()
    const nextBody = replaceDisplayRange(start, replaceEnd, "")
    options.text.value = nextBody
    options.onInput(new Event("input"))
    openCreateFormForAtMention(mode, start)
  }

  function selectAtSuggestion(item: AtMentionSuggestion) {
    const storedStart = slashStart.value
    const storedEnd = slashEnd.value
    if (storedStart == null || storedEnd == null || !item.key) {
      closeMenu()
      return
    }

    const displayText = walkEditor("display")
    const cursor = getCaretOffset()
    const end = Math.min(cursor, storedEnd, displayText.length)

    closeMenu()
    insertAtMentionAtRange(storedStart, end, item)
  }

  async function resolveLocation(name: string, address: string): Promise<Location | null> {
    if (!locationController) return null
    const trimmedName = name.trim()
    const trimmedAddress = address.trim()
    try {
      const query = new ODataQueryBuilder().ne("name", null).top(1)
      if (trimmedName) {
        query.eq("name", trimmedName)
      } else if (trimmedAddress) {
        query.eq("address", trimmedAddress)
      }
      const found = await locationController.getList({ query })
      if (found[0]?.id) return found[0]
    } catch {
      /* fall through */
    }
    try {
      // The address is geocoded server-side on create, so the location comes
      // back with coordinates without the client needing to resolve them.
      const location = new Location({
        name: trimmedName || null,
        address: trimmedAddress || null,
        isTenantBase: false
      } as any)
      return (await locationController.create(location)) ?? location
    } catch {
      return null
    }
  }

  function createPendingLocationId(label: string): string {
    const id = uuidv4()
    pendingLocationIds.add(id)
    pendingLocationLabels.set(id, stripCountryFromLocationText(label))
    return id
  }

  function replaceLocationMentionId(pendingId: string, realId: string) {
    clearPendingLocation(pendingId)
    const body = options.text.value ?? ""
    const token = `{{location:${pendingId}}}`
    if (!body.includes(token)) return

    options.text.value = body.replace(token, `{{location:${realId}}}`)
    editorEl.value?.querySelectorAll(`[data-location-id="${pendingId}"]`).forEach((node) => {
      const el = node as HTMLElement
      el.dataset.locationId = realId
      delete el.dataset.saving
      el.className = mentionLinkClass("location")
      el.textContent = mention.labelFor("location", realId)
      el.title = LOCATION_MENTION_HOVER_TITLE
    })
    ensureLoaded(realId)
    options.onInput(new Event("input"))
  }

  function replaceLocationMentionWithText(pendingId: string, text: string) {
    clearPendingLocation(pendingId)
    const body = options.text.value ?? ""
    const token = `{{location:${pendingId}}}`
    if (!body.includes(token)) return

    options.text.value = body.replace(token, text)
    editorEl.value
      ?.querySelectorAll(`[data-location-id="${pendingId}"]`)
      .forEach((node) => node.replaceWith(document.createTextNode(text)))
    options.onInput(new Event("input"))
  }

  async function finalizePendingLocationMention(pendingId: string, item: LocationSuggestion) {
    const label = stripCountryFromLocationText(item.label)
    try {
      const location = await resolveLocation("", label)
      if (!location?.id) {
        replaceLocationMentionWithText(pendingId, label)
        return
      }
      ModelManager.getInstance().updateOrCreate(Location, location)
      replaceLocationMentionId(pendingId, location.id)
    } catch {
      replaceLocationMentionWithText(pendingId, label)
    }
  }

  function insertLocationMentionAtRange(replaceStart: number, end: number, locationId: string) {
    const labelLength = mention.labelFor("location", locationId).length
    replaceDisplayRange(replaceStart, end, `{{location:${locationId}}}${mentionTrailingSpace}`)
    setCaretByDisplayOffset(caretAfterMention(replaceStart, labelLength))
    options.text.value = walkEditor("body")
    options.onInput(new Event("input"))
    nextTick(focus)
  }

  function selectSuggestion(item: LocationSuggestion) {
    const storedStart = slashStart.value
    const storedEnd = slashEnd.value
    if (storedStart == null || storedEnd == null) {
      closeMenu()
      return
    }

    const displayText = walkEditor("display")
    const cursor = getCaretOffset()
    const end = Math.min(cursor, storedEnd, displayText.length)

    open.value = false
    items.value = []
    query.value = ""
    slashStart.value = null
    slashEnd.value = null
    locationTrigger.value = null

    if (item.source === "saved" && item.location?.id) {
      insertLocationMentionAtRange(storedStart, end, item.location.id)
      return
    }

    const pendingId = createPendingLocationId(item.label)
    insertLocationMentionAtRange(storedStart, end, pendingId)
    void finalizePendingLocationMention(pendingId, item)
  }

  async function selectHighlightedOrFirstSuggestion() {
    if (selectorMode.value === "at") {
      let item = atItems.value[highlightedIndex.value] ?? atItems.value[0]
      if (!item && trimmedQuery.value.length >= AT_MENTION_AUTOCOMPLETE_MIN_CHARS) {
        delayedSearch.cancel()
        const results = await runAtSearch()
        item = results[0]
      }
      if (item) selectAtSuggestion(item)
      return
    }

    let item = items.value[highlightedIndex.value] ?? items.value[0]
    if (!item && trimmedQuery.value.length >= MIN_CHARS) {
      delayedSearch.cancel()
      const results = (await runSearch()) as LocationSuggestion[]
      item = results[0]
    }
    if (item) selectSuggestion(item)
  }

  function onMapSaved(saved: Location) {
    mention.closeMap()
    if (saved.id) refreshLocationMentionLabel(saved.id)
    if (!isFocused.value) renderEditor(options.text.value ?? "")
  }

  function focus() {
    editorEl.value?.focus()
  }

  function insertUploadedFile(fileRelation: FileRelation) {
    const offset = pendingFileOffset.value
    pendingFileOffset.value = null
    if (offset == null || !fileRelation?.id) return
    const labelLength = mention.labelFor("file", fileRelation.id).length
    replaceDisplayRange(offset, offset, `{{file:${fileRelation.id}}}${mentionTrailingSpace}`)
    setCaretByDisplayOffset(caretAfterMention(offset, labelLength))
    options.text.value = walkEditor("body")
    options.onInput(new Event("input"))
  }

  return {
    MIN_CHARS,
    rootEl,
    editorEl,
    isFocused,
    open,
    loading,
    items,
    atItems,
    atMentionMode,
    selectorMode,
    highlightedIndex,
    selectorTop,
    trimmedQuery,
    mapLocation: mention.mapLocation,
    mapAnchor: mention.mapAnchor,
    closeMap: mention.closeMap,
    usesInlineLocationMap: mention.usesInlineLocationMap,
    onMapSaved,
    handleInput,
    handleKeydown,
    handlePaste,
    handleEditorClick,
    handleEditorDblClick,
    handleFocusout,
    selectSuggestion,
    selectAtSuggestion,
    focus,
    insertUploadedFile
  }
}
